export interface GridSpan {
  cols: number;
  rows: number;
}

export interface CellPosition {
  col: number;
  row: number;
}

/**
 * Simulate CSS `grid-auto-flow: row dense` placement: walk the ordered items and
 * drop each into the earliest (top-most, then left-most) free cell that fits its
 * span, backfilling gaps left by larger tiles. Returns each item's top-left cell.
 *
 * This mirrors what the browser does so the drag preview can shift tiles to the
 * positions they'll actually occupy after a reorder, instead of assuming uniform
 * 1x1 cells (which ignores wide/tall/large spans).
 */
export const packDenseGrid = <T>(
  order: T[],
  spanOf: (item: T) => GridSpan,
  columns: number
): Map<T, CellPosition> => {
  const cols = Math.max(1, columns);
  const occupied: boolean[][] = [];

  const ensureRow = (row: number) => {
    while (occupied.length <= row) {
      occupied.push(new Array<boolean>(cols).fill(false));
    }
  };

  const fits = (row: number, col: number, span: GridSpan): boolean => {
    if (col + span.cols > cols) {
      return false;
    }
    for (let dr = 0; dr < span.rows; dr++) {
      ensureRow(row + dr);
      for (let dc = 0; dc < span.cols; dc++) {
        if (occupied[row + dr][col + dc]) {
          return false;
        }
      }
    }
    return true;
  };

  const fill = (row: number, col: number, span: GridSpan) => {
    for (let dr = 0; dr < span.rows; dr++) {
      ensureRow(row + dr);
      for (let dc = 0; dc < span.cols; dc++) {
        occupied[row + dr][col + dc] = true;
      }
    }
  };

  const result = new Map<T, CellPosition>();

  for (const item of order) {
    const raw = spanOf(item);
    const span: GridSpan = {cols: Math.min(raw.cols, cols), rows: Math.max(1, raw.rows)};

    for (let row = 0; ; row++) {
      ensureRow(row);
      let placed = false;
      for (let col = 0; col < cols; col++) {
        if (fits(row, col, span)) {
          fill(row, col, span);
          result.set(item, {col, row});
          placed = true;
          break;
        }
      }
      if (placed) {
        break;
      }
    }
  }

  return result;
};
